import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { DepartmentNav } from "../components/DepartmentNav";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

/**
 * AdminFoundersPage — one-click promote a user to "Founding Member" tier.
 *
 * Used to onboard the first ~10 inbound DMs from social-media beta seeding.
 * Type the email, click promote, the backend flips the tier + auto-sends
 * the welcome email with kit Amazon links and the season-long deal.
 *
 * Only renders for admins (gated by <AdminRoute /> in App.js).
 */
const AdminFoundersPage = () => {
  const [email, setEmail] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [members, setMembers] = useState([]);
  const [count, setCount] = useState(0);
  const [cap, setCap] = useState(10);
  const [remaining, setRemaining] = useState(10);
  const [loadingList, setLoadingList] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const authConfig = useCallback(
    (extra = {}) => ({ withCredentials: true, ...extra }),
    []
  );

  const fetchMembers = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/founding-members`,
        authConfig()
      );
      setMembers(res.data.members || []);
      setCount(res.data.count || 0);
      setCap(res.data.cap || 10);
      setRemaining(res.data.remaining_slots ?? 10);
    } catch (e) {
      toast.error("Couldn't load founders list — check admin login.");
    } finally {
      setLoadingList(false);
    }
  }, [authConfig]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handlePromote = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      toast.error("Enter a valid email address first.");
      return;
    }
    if (remaining === 0 && !members.find((m) => m.email === cleanEmail)) {
      const confirmed = window.confirm(
        "You've already filled all 10 founding slots. Promote anyway?"
      );
      if (!confirmed) return;
    }
    setPromoting(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${encodeURIComponent(cleanEmail)}/promote-founder`,
        {},
        authConfig()
      );
      const data = res.data;
      setLastResult(data);
      const emailStatus = data.welcome_email?.status;
      if (data.was_already_founder) {
        toast.success(
          `${cleanEmail} was already a founder — re-sent welcome email (${emailStatus}).`
        );
      } else if (emailStatus === "sent") {
        toast.success(
          `🎉 ${cleanEmail} promoted! Welcome email delivered.`
        );
      } else if (emailStatus === "skipped") {
        toast.warning(
          `Tier flipped, but welcome email skipped: ${data.welcome_email?.error || data.welcome_email?.reason || "unknown"}`
        );
      } else {
        toast.success(`${cleanEmail} promoted to Founding Member.`);
      }
      setEmail("");
      fetchMembers();
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Promotion failed.";
      toast.error(`Couldn't promote: ${msg}`);
      setLastResult({ error: msg });
    } finally {
      setPromoting(false);
    }
  };

  const handleRemove = async (founderEmail) => {
    const ok = window.confirm(
      `Remove ${founderEmail} from founders? They'll be reverted to Free tier.`
    );
    if (!ok) return;
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(founderEmail)}/tier`,
        { tier: "free" },
        authConfig()
      );
      toast.success(`${founderEmail} removed from founders.`);
      fetchMembers();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Removal failed.";
      toast.error(`Couldn't remove: ${msg}`);
    }
  };

  const previewEmail = () => {
    // Backend admin preview endpoint requires admin session cookie.
    // Open in a new tab — the browser will send the cookie automatically.
    const win = window.open(
      `${API_URL}/api/admin/transactional/preview/founder_welcome`,
      "_blank"
    );
    if (!win) {
      toast.error("Popup blocked — allow popups for admin previews.");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div data-testid="admin-founders-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <main className="max-w-3xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-4">
          <img
            src="/brand/turfpulse-inner-circle.svg"
            alt="TurfPulse Inner Circle crest"
            className="h-16 w-16 flex-shrink-0"
          />
          <div>
            <h1 className="text-2xl font-bold">Inner Circle</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Promote inbound DMs from FB / social to full Inner Circle access.
              One click → tier flipped + welcome email sent with kit links and the season-long deal.
            </p>
          </div>
        </div>

        {/* Slot counter */}
        <Card data-testid="founders-slot-counter">
          <CardContent className="flex items-center justify-between py-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">
                Inner Circle members
              </div>
              <div className="text-4xl font-bold text-zinc-900 leading-none">
                {count}
                <span className="text-lg text-muted-foreground font-medium"> / {cap}</span>
              </div>
            </div>
            <div
              className={`text-right ${
                remaining === 0 ? "text-amber-700" : remaining <= 3 ? "text-amber-600" : "text-emerald-600"
              }`}
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-1">
                Slots remaining
              </div>
              <div className="text-3xl font-bold leading-none">{remaining}</div>
            </div>
          </CardContent>
        </Card>

        {/* Promote form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Promote a user</CardTitle>
            <CardDescription>
              They must already have a TurfPulse account. Sends the welcome email automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePromote} className="space-y-3">
              <div>
                <Input
                  type="email"
                  placeholder="michael@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={promoting}
                  data-testid="founder-promote-email-input"
                  required
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  disabled={promoting || !email.trim()}
                  data-testid="founder-promote-submit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
                >
                  {promoting ? "Welcoming…" : "Welcome to the Inner Circle"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={previewEmail}
                  data-testid="founder-preview-email"
                >
                  Preview welcome email
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={fetchMembers}
                  disabled={loadingList}
                  data-testid="founder-refresh-list"
                >
                  {loadingList ? "Refreshing…" : "Refresh list"}
                </Button>
              </div>
            </form>

            {lastResult && !lastResult.error && (
              <div
                data-testid="founder-promote-result"
                className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm"
              >
                <div className="font-semibold text-emerald-900 mb-1">
                  {lastResult.was_already_founder ? "Re-sent welcome email" : "Tier flipped"}
                </div>
                <div className="text-emerald-800">
                  Email: <code className="text-xs">{lastResult.email}</code>
                </div>
                <div className="text-emerald-800">
                  Welcome email status:{" "}
                  <span className="font-mono text-xs">{lastResult.welcome_email?.status || "unknown"}</span>
                  {lastResult.welcome_email?.error && (
                    <span className="text-rose-700 ml-2">
                      ({lastResult.welcome_email.error})
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current members list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Current Inner Circle</CardTitle>
            <CardDescription>
              Sorted by join date. Every member equal — no rankings, no rookie/veteran split.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingList && (
              <div className="text-sm text-muted-foreground py-6">Loading…</div>
            )}
            {!loadingList && members.length === 0 && (
              <div
                data-testid="founders-empty-state"
                className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md"
              >
                No Inner Circle members yet. Type an email above to welcome the first.
              </div>
            )}
            {!loadingList && members.length > 0 && (
              <div data-testid="founders-list" className="divide-y divide-border">
                {members.map((m, idx) => (
                  <div
                    key={m.email}
                    data-testid={`founder-row-${idx}`}
                    className="flex items-center justify-between py-3 text-sm gap-3"
                  >
                    <img
                      src="/brand/turfpulse-inner-circle.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-8 w-8 flex-shrink-0 opacity-90"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-zinc-900 truncate">
                        {m.name || m.email.split("@")[0]}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-muted-foreground">
                        Joined {fmtDate(m.founding_member_since)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Free until {fmtDate(m.founding_expires_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(m.email)}
                      data-testid={`founder-remove-${idx}`}
                      className="ml-2 text-[11px] text-rose-700 hover:text-rose-900 hover:underline font-medium flex-shrink-0"
                      title="Demote this founder back to Free tier"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-muted-foreground italic pt-2">
          Heads up: the welcome email needs Resend's API key in production +
          a verified sending domain. If "Welcome email status" shows{" "}
          <code>skipped</code> after promotion, fix that before DM'ing more
          founders or they'll get the badge but no email.
        </div>
      </main>
    </div>
  );
};

export default AdminFoundersPage;
export { AdminFoundersPage };
